import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ColumnFilter,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  DecimalScale,
  FilterType,
  FormDialogMode,
  Input,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  makeStyles,
  Typography,
  useTheme
} from '@material-ui/core'
import BlockIcon from '@material-ui/icons/Block'
import SendIcon from '@material-ui/icons/Send'
import moment from 'moment'
import {
  EnumDispatchType,
  EnumOperationState,
  EnumTipoOperacion,
  EnumZonaBalance
} from '../../api/oData/enums'
import CustomFilter from '../../components/shared/customFilter/customFilter'
import DateFilter from '../../components/shared/dateFilter/dateFilter'
import {
  enumToSelectOptionArray,
  enumToSelectOptionArrayDefaultValues,
  enumValueToKey
} from '../../helpers'
import { generateIconsFromConfiguration } from '../../helpers/dataTableActionIconHelper'
import { ActionIcons, DateFilters, NonAuthRoutes } from '../../model/enums'
import NominationListItemViewModel, {
  AddMassiveNominationModel,
  FlangeViewModel
} from './nomination.model'
import {
  addMassive,
  addNomination,
  canCancelMassive,
  cancel,
  cancelMassive,
  canDeleteMassive,
  canSendMassive,
  checkOperationsState,
  countNominationListItems,
  deleteMassive,
  deleteNomination,
  editNomination,
  fetchLogisticElementsToItems,
  fetchNominationListItems,
  filterApplicableActionsConfiguration,
  send,
  sendMassive,
  fetchNominationListItemsV2,
  fetchCompatibleLogisticElementsSelectOption,
  fetchLogisticElementsSelectOption
} from './nomination.service'
import { styles } from './nomination.styles'

import { Add, Delete, Edit, PlusOne } from '@material-ui/icons'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { NotificationMessageIcons } from '../../components'
import { ErrorDetailList } from '../../components/errorDetailList/errorDetailList'
import { DataTableErrorHandler } from '../../components/errors/scopes/dataTable/dataTableError'
import Page from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import { normalizeFilename } from '../../helpers/fileDownloadHelper'
import { getGasDay } from '../../helpers/gasDayHelper'
import { getUserPermissions } from '../../helpers/permissionsHelper'
import { usePageState } from '../../hooks'
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog'
import { useErrorDetailListDialog } from '../../hooks/useErrorDetailListDialog'
import { FormDialogDefinitionProps, useFormDialog } from '../../hooks/useFormDialog'
import { useHttpError } from '../../hooks/useHttpError'
import useRetries from '../../hooks/useRetries'
import { DatePeriod, GlobalNotification } from '../../model'
import { ConfirmationDialogs } from '../../model/enums/confirmationDialogs'
import { Pages } from '../../model/RBAC/constants'
import { ServiceBatchResponse } from '../../model/serviceBatchResponse'
import {
  getActiveBalanceZone,
  getCustomerDomain,
  notifyError,
  RootStoreModel,
  storeCustomFilterForCurrentPage,
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage,
  storeDateFilterForCurrentPage
} from '../../redux'
import NominationModel from './nomination.model'
import CustomFilterV2 from '../../components/shared/customFilter/customFilterV2'
import { EnumOperationType } from '../../api'
import { OnEventReceived } from '@globaleris/ui.library/src/lib'
import { StatusBalanceResponse } from '../balance/balances.model'
import { getStatusBalance } from '../balance/balances.services'

export interface NominationPageProps {}

export const NominationPage: React.FC<NominationPageProps> = () => {
  const [t] = useTranslation([
    'globalerisLibrary',
    'translation',
    'nomination',
    'balance',
    'enums',
    'errorDetailList'
  ])

  let history = useHistory()

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  // const { info } = useNotify()
  const { info, getEventsReceived, addEventListener, deleteEventListener } = useNotify()
  const [balanceStatus, setBalanceStatus] = useState<StatusBalanceResponse>({ inProcess: false })
  const balanceInProcess = useRef<boolean | undefined>(undefined)
  const nominationsNeedUpdate = useRef<boolean>(false)

  const nominationFilterESP = [
    EnumTipoOperacion.DemandaLLDD,
    EnumTipoOperacion.DemandaLLDDME,
    EnumTipoOperacion.DemandaConvencional,
    EnumTipoOperacion.EntradaRtPCI,
    EnumTipoOperacion.EntradaRtPCPR,
    EnumTipoOperacion.EntradaRtPCY,
    EnumTipoOperacion.Entrada_RT_PPBD,
    EnumTipoOperacion.Extracción,
    EnumTipoOperacion.Inyección,
    EnumTipoOperacion.Licuefaccion,
    EnumTipoOperacion.Mermas,
    EnumTipoOperacion.Regasificacion,
    EnumTipoOperacion.SalidaRtPCI
  ]

  const nominationFilterPT = [
    EnumTipoOperacion.EntradaRNTGN,
    EnumTipoOperacion.SaídaRNTGN,
    EnumTipoOperacion.Descarga,
    EnumTipoOperacion.DescargaSimples,
    EnumTipoOperacion.Recarga,
    EnumTipoOperacion.RecargaSimples,
    EnumTipoOperacion.EntregaGN,
    EnumTipoOperacion.RecepcionGN,
    EnumTipoOperacion.RegasificacionPT,
    EnumTipoOperacion.InyeccionPT,
    EnumTipoOperacion.ExtraccionPT,
    EnumTipoOperacion.LicuefaccionPT,
    EnumTipoOperacion.EntradaRNTGNPCI,
    EnumTipoOperacion.EntradaRNTGNAASS,
    EnumTipoOperacion.SaidaRNTGNPCI,
    EnumTipoOperacion.SaidaRNTGNAASS
  ]

  const getNominationFilter = () => {
    let activeBZ = getActiveBalanceZone()?.id

    return activeBZ == EnumZonaBalance.ESP
      ? nominationFilterESP
      : activeBZ == EnumZonaBalance.PT
        ? nominationFilterPT
        : nominationFilterESP
  }

  const flangesFilter = [EnumTipoOperacion.Compra, EnumTipoOperacion.Venta]

  const { dataTableFilter, dataTableSearch, dateFilter, customFilter, dataTableRowsPerPage } =
    usePageState()

  const activeCompany = useSelector(
    (state: RootStoreModel) => state.profile?.activeCompany && state.profile?.activeCompany
  )

  const activeBalanceZone = useSelector(
    (state: RootStoreModel) => state.profile?.activeBalanceZone && state.profile?.activeBalanceZone
  )

  const [dateRange, setDateRange] = React.useState<DatePeriod>({
    start: dateFilter ? moment(dateFilter.initDate).toDate() : moment().toDate(),
    end: dateFilter ? moment(dateFilter.endDate).toDate() : moment().toDate()
  })

  const [isFetching, setIsFetching] = useState<boolean>(true)
  const [isFetchingLogisticElements, setIsFetchingLogisticElements] = useState<boolean>(false)
  const selectedOperationTypes = useRef<EnumTipoOperacion[]>([])

  const [nominations, setNominations] = useState<NominationModel[] | null>(null)
  const [dataTableKey, setDataTableKey] = useState<number>(0)
  const [flanges, setFlanges] = useState<FlangeViewModel[] | undefined>(undefined)

  const [showFlangeDialog, setShowFlangeDialog] = useState<boolean>(false)

  const [globalNotification, setGlobalNotification] = React.useState<
    GlobalNotification | undefined
  >(undefined)

  const [logisticElementsForCustomFilter, setLogisticElementsForCustomFilter] = React.useState<
    SelectOption[]
  >([])

  // Map of logisticElements to operationTypes
  const [logisticElementsSelectOptions, setLogisticElementsToOperationTypes] = useState<
    SelectOption[]
  >([])

  interface formDependenciesProps {
    formState: any
    logisticElements: SelectOption[]
  }

  const [formDependencies, setFormDependencies] = React.useState<formDependenciesProps>({
    formState: {},
    logisticElements: []
  })

  //#region Form definition

  const addFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'ADD',
        inputs: [
          {
            name: 'date',
            label: t('nomination:forms.crud.inputs.date'),
            type: DataType.DATE,
            readOnly: false,
            required: true,
            minDate: getGasDay().toISOString(),
            defaultValue: moment(dateRange.start).isBefore(moment()) ? new Date() : dateRange.start
          },
          {
            name: 'programmingType',
            label: t('nomination:forms.crud.inputs.operationType'),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArrayDefaultValues(
              EnumTipoOperacion,
              'enums:EnumTipoOperacion',
              getNominationFilter()
            ),
            readOnly: false,
            required: true,
            onChange: async (value: any, formState: object) => {
              setFormWaiting(true)
              setFormDependencies({
                ...formDependencies,
                logisticElements: await fetchLogisticElementsToItems([
                  enumValueToKey(EnumTipoOperacion, value)
                ]),
                formState: formState
              })
            }
          },
          {
            name: 'logisticElement.id',
            label: t('nomination:forms.crud.inputs.logisticElement'),
            type: DataType.SELECT,
            selectOptions: formDependencies.logisticElements,
            readOnly: false,
            required: true,
            helpText: t('nomination:forms.add.helpTexts.logisticElement')
          },
          {
            name: 'quantity',
            label: t('nomination:forms.crud.inputs.quantity'),
            type: DataType.NUMBER,
            readOnly: false,
            decimalScale: DecimalScale.ENERGY,
            required: true
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formDependencies])

  const editDraftFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT_DRAFT',
        inputs: [
          {
            name: 'date',
            label: t('nomination:forms.crud.inputs.date'),
            type: DataType.DATE,
            defaultValue: moment().toDate(),
            readOnly: false,
            required: true,
            disablePast: true
          },
          {
            name: 'programmingType',
            label: t('nomination:forms.crud.inputs.operationType'),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArrayDefaultValues(
              EnumTipoOperacion,
              'enums:EnumTipoOperacion',
              getNominationFilter()
            ),
            readOnly: false,
            required: true,
            onChange: async (value: any, formState: object) => {
              setFormWaiting(true)
              setFormDependencies({
                ...formDependencies,
                logisticElements: await fetchLogisticElementsToItems([
                  enumValueToKey(EnumTipoOperacion, value)
                ]),
                formState: formState
              })
            }
          },
          {
            name: 'logisticElement.id',
            label: t('nomination:forms.crud.inputs.logisticElement'),
            type: DataType.SELECT,
            selectOptions: formDependencies.logisticElements,
            readOnly: false,
            required: true,
            helpText: t('nomination:forms.edit.helpTexts.logisticElement')
          },
          {
            name: 'quantity',
            label: t('nomination:forms.crud.inputs.quantity'),
            type: DataType.NUMBER,
            readOnly: false,
            decimalScale: DecimalScale.ENERGY,
            required: true
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formDependencies])

  const editFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT',
        inputs: [
          {
            name: 'date',
            label: t('nomination:forms.crud.inputs.date'),
            type: DataType.DATE,
            defaultValue: moment().toDate(),
            readOnly: true,
            required: true,
            disablePast: true
          },
          {
            name: 'programmingType',
            label: t('nomination:forms.crud.inputs.operationType'),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArrayDefaultValues(
              EnumTipoOperacion,
              'enums:EnumTipoOperacion',
              getNominationFilter()
            ),
            required: true,
            onChange: async (value: any, formState: object) => {
              setFormWaiting(true)
              setFormDependencies({
                ...formDependencies,
                logisticElements: await fetchLogisticElementsToItems([
                  enumValueToKey(EnumTipoOperacion, value)
                ]),
                formState: formState
              })
            }
          },
          {
            name: 'logisticElement.id',
            label: t('nomination:forms.crud.inputs.logisticElement'),
            type: DataType.SELECT,
            selectOptions: formDependencies.logisticElements,
            required: true,
            helpText: t('nomination:forms.edit.helpTexts.logisticElement')
          },
          {
            name: 'quantity',
            label: t('nomination:forms.crud.inputs.quantity'),
            type: DataType.NUMBER,
            decimalScale: DecimalScale.ENERGY,
            required: true
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formDependencies])

  const addMassiveFormDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'ADD_MASSIVE',
        inputs: [
          {
            name: 'startDate',
            label: t('nomination:forms.addMassive.inputs.startDate'),
            type: DataType.DATE,
            readOnly: false,
            required: true,
            minDate: getGasDay().toISOString(),
            defaultValue: moment(dateRange.start).isBefore(moment()) ? new Date() : dateRange.start,
            onChange: (value, formState: any) => {
              if (!moment(value).isValid()) {
                return
              }
              if (formState.endDate && moment(formState.endDate).isBefore(moment(value))) {
                setFormDependencies({
                  ...formDependencies,
                  formState: {
                    ...formState,
                    startDate: moment(value).toDate(),
                    endDate: moment(formState.startDate).toDate()
                  }
                })
              } else {
                setFormDependencies({
                  ...formDependencies,
                  formState: {
                    ...formState,
                    startDate: moment(value).toDate(),
                    endDate:
                      formState.endDate !== undefined
                        ? moment(formState.endDate).toDate()
                        : undefined
                  }
                })
              }
            }
          },
          {
            name: 'endDate',
            label: t('nomination:forms.addMassive.inputs.endDate'),
            type: DataType.DATE,
            readOnly: false,
            required: true,
            minDate: formDependencies.formState?.startDate
              ? formDependencies.formState?.startDate?.toISOString()
              : undefined,
            defaultValue: moment(dateRange.end).isBefore(moment(dateRange.start))
              ? dateRange.start
              : dateRange.end,
            onChange: (value, formState: any) => {
              if (!moment(value).isValid()) {
                return
              }
              if (formState.startDate && moment(value).isBefore(moment(formState.startDate))) {
                setFormDependencies({
                  ...formDependencies,
                  formState: { ...formState, endDate: moment(formState.startDate).toDate() }
                })
              } else {
                setFormDependencies({
                  ...formDependencies,
                  formState: {
                    ...formState,
                    endDate: moment(value).toDate(),
                    startDate:
                      formState.startDate !== undefined
                        ? moment(formState.startDate).toDate()
                        : undefined
                  }
                })
              }
            }
          },
          {
            name: 'programmingType',
            label: t('nomination:forms.addMassive.inputs.operationType'),
            type: DataType.SELECT,
            selectOptions: enumToSelectOptionArrayDefaultValues(
              EnumTipoOperacion,
              'enums:EnumTipoOperacion',
              getNominationFilter()
            ),
            readOnly: false,
            required: true,
            onChange: async (value: any, formState: object) => {
              setFormWaiting(true)
              setFormDependencies({
                ...formDependencies,
                logisticElements: await fetchLogisticElementsToItems([
                  enumValueToKey(EnumTipoOperacion, value)
                ]),
                formState: formState
              })
            }
          },
          {
            name: 'logisticElement.id',
            label: t('nomination:forms.addMassive.inputs.logisticElement'),
            type: DataType.SELECT,
            selectOptions: formDependencies.logisticElements,
            readOnly: false,
            required: true,
            helpText: t('nomination:forms.add.helpTexts.logisticElement')
          },
          {
            name: 'quantity',
            label: t('nomination:forms.addMassive.inputs.quantity'),
            type: DataType.NUMBER,
            readOnly: false,
            decimalScale: DecimalScale.ENERGY,
            required: true
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formDependencies])

  //#endregion

  //#region Hooks

  const { showErrorDetailListDialog, errorDetailListDialogProps } = useErrorDetailListDialog()

  const { showError, clearError, errorProps } = useHttpError()

  const { openForm, updateFormDefinition, closeForm, setFormWaiting, formProps, CustomFormDialog } =
    useFormDialog()

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const [startRetriesTimer, stopRetriesTimer] = useRetries(
    async (pendingOperations: NominationListItemViewModel[]) => {
      pendingOperations = await monitorOperations(pendingOperations)

      if (pendingOperations.length === 0) {
        stopRetriesTimer()
        setGlobalNotification(undefined)
        info(t('nomination:notifications.success.retriesTSOFinish'))
        return
      }
    },
    5000,
    {
      immediate: true,
      onRetry: count => {
        setGlobalNotification({
          icon: NotificationMessageIcons.LOADING,
          message: t('nomination:globalNotifications.retries.tso.messages.onRetry'),
          actions: [
            {
              label: t('nomination:globalNotifications.retries.tso.actions.cancel'),
              onClick: () => {
                stopRetriesTimer()
                setGlobalNotification({
                  message: t('nomination:globalNotifications.retries.tso.messages.onStop'),
                  actions: [
                    {
                      label: t('nomination:globalNotifications.retries.tso.actions.refresh'),
                      onClick: async () => {
                        getNominations()
                      }
                    },
                    {
                      label: t('nomination:globalNotifications.retries.tso.actions.close'),
                      onClick: () => {
                        setGlobalNotification(undefined)
                      }
                    }
                  ]
                })
              }
            }
          ]
        })
      },
      onFinishRetries: () => {
        setGlobalNotification({
          icon: NotificationMessageIcons.INFO,
          message: t('nomination:globalNotifications.retries.tso.messages.onFinish'),
          actions: [
            {
              label: t('nomination:globalNotifications.retries.tso.actions.refresh'),
              onClick: async () => {
                getNominations()
              }
            },
            {
              label: t('nomination:globalNotifications.retries.tso.actions.close'),
              onClick: () => {
                setGlobalNotification(undefined)
              }
            }
          ]
        })
      },
      onError: error => {
        setGlobalNotification({
          icon: NotificationMessageIcons.ERROR,
          message: t('nomination:globalNotifications.retries.tso.messages.onError'),
          actions: [
            {
              label: t('nomination:globalNotifications.retries.tso.actions.refresh'),
              onClick: async () => {
                getNominations()
              }
            }
          ]
        })
      }
    }
  )

  //#region Handlers

  const onSelectDateHandler = (
    startDate: Date,
    endDate: Date,
    activeDateFilter?: DateFilters
  ): void => {
    setDateRange({ start: startDate, end: endDate })
    storeDateFilterForCurrentPage({
      activeDateFilter: activeDateFilter,
      initDate: startDate,
      endDate: endDate
    })
  }

  const onAddNominationClickHandler = () => {
    openForm({
      definition: addFormDefinition,
      title: t('nomination:forms.crud.titles.add'),
      mode: FormDialogMode.ADD,
      isWaiting: true,
      onClose: onAddNominationHandler
    })

    setFormDependencies({
      ...formDependencies,
      formState: {},
      logisticElements: []
    })
  }

  const onAddNominationHandler = async (nomination: NominationModel) => {
    if (nomination) {
      setFormWaiting(true)
      addNomination(nomination).then(async response => {
        if (response.success) {
          getNominations()

          info(t('nomination:notifications.success.addMessage'))
        } else {
          response.error
            ? showErrorDetailListDialog(
                t('nomination:notifications.error.addError'),
                [response.error],
                moment(nomination.date).format('DD/MM/YYYY')
              )
            : notifyError(t('nomination:notifications.error.getDataError'))
        }
        closeForm()
      })
    } else {
      closeForm()
    }
  }

  const onAddMassiveNominationsClickHandler = () => {
    const filter = customFilter ? customFilter : dateRange
    openForm({
      definition: addMassiveFormDefinition,
      title: t('nomination:forms.addMassive.title'),
      mode: FormDialogMode.ADD,
      onClose: onAddMassiveNominationHandler
    })

    setFormDependencies({
      ...formDependencies,
      formState: {},
      logisticElements: []
    })
  }

  const onAddMassiveNominationHandler = async (data: AddMassiveNominationModel) => {
    if (data) {
      const daysDifference = moment(data.endDate).diff(moment(data.startDate), 'days') + 1
      if (daysDifference <= 100) {
        // #71523 - El Batch sólo soporta un máximo de 100 items. Si se necesita superarlo, habría que pasar la llamada a través de pioneer y que este fraccione las llamadas.
        setFormWaiting(true)
        addMassive(data).then(async (response: ServiceBatchResponse) => {
          if (response.success) {
            info(t('nomination:notifications.success.addMassive'))
          } else {
            showErrorDetailListDialog(
              t('nomination:notifications.error.addMassive'),
              response.errors
            )
          }
          getNominations()
          closeForm()
        })
      } else {
        info(t('nomination:notifications.info.addMaxError'))
      }
    } else {
      closeForm()
    }
  }

  const onEditNominationClickHandler = async (nomination: NominationModel) => {
    openForm({
      definition: editFormDefinition,
      title: t('nomination:forms.crud.titles.edit'),
      mode: FormDialogMode.EDIT,
      isWaiting: true,
      onClose: onEditNominationHandler
    })

    setFormDependencies({
      ...formDependencies,
      logisticElements: await fetchLogisticElementsToItems([
        enumValueToKey(EnumTipoOperacion, nomination.programmingType)
      ]),
      formState: nomination
    })
  }

  const onEditDraftNominationClickHandler = async (nomination: NominationModel) => {
    openForm({
      definition: editDraftFormDefinition,
      title: t('nomination:forms.crud.titles.edit'),
      mode: FormDialogMode.EDIT,
      isWaiting: true,
      onClose: onEditNominationHandler
    })

    setFormDependencies({
      ...formDependencies,
      logisticElements: await fetchLogisticElementsToItems([
        enumValueToKey(EnumTipoOperacion, nomination.programmingType)
      ]),
      formState: nomination
    })
  }

  const onEditNominationHandler = async (nomination: NominationModel) => {
    if (nomination) {
      setFormWaiting(true)
      nomination.state = Array.isArray(nomination.state) ? nomination.state[0] : nomination.state
      await editNomination(nomination).then(async response => {
        if (response.success) {
          getNominations()
          info(t('nomination:notifications.success.modifyMessage'))
        } else {
          response.error
            ? showErrorDetailListDialog(
                t('nomination:notifications.error.modifyError'),
                [response.error],
                moment(nomination.date).format('DD/MM/YYYY')
              )
            : notifyError(t('nomination:notifications.error.getDataError'))
        }
        closeForm()
      })
    } else {
      closeForm()
    }
  }

  const onViewFlangesHandler = (item: NominationModel) => {
    setShowFlangeDialog(true)
    setFlanges(item.flanges)
  }

  const onAllocateActionHandler = (item: NominationModel) => {
    history.push(NonAuthRoutes.allocationsEdit, {
      id: item.id,
      dispatchType: EnumDispatchType.Reparto02G
    })
  }

  const onCancelMassiveActionHandler = async (items: NominationModel[]) => {
    if (canCancelMassive(items)) {
      getConfirmation(ConfirmationDialogs.CANCEL_MASSIVE).then(async () => {
        await cancelMassive(items).then(async (response: ServiceBatchResponse) => {
          if (response.success) {
            info(t('nomination:notifications.success.cancelMassive'))
          } else {
            showErrorDetailListDialog(
              t('nomination:notifications.error.cancelMassive'),
              response.errors
            )
          }
          getNominations()
        })

        closeConfirmation()
      })
    } else {
      info(t('nomination:notifications.info.cancelError'))
    }
  }

  const onCancelActionHandler = async (item: NominationModel) => {
    item.quantity = 0
    item.state = EnumOperationState.PendienteRenvio
    getConfirmation(ConfirmationDialogs.CANCEL).then(async () => {
      await cancel(item).then(async response => {
        if (response.success) {
          info(t('nomination:notifications.success.cancelMessage'))
          getNominations()
        } else {
          response.error
            ? showErrorDetailListDialog(
                t('nomination:notifications.error.cancelError'),
                [response.error],
                moment(item.date).format('DD/MM/YYYY')
              )
            : notifyError(t('nomination:notifications.error.getDataError'))
        }
      })
      closeConfirmation()
    })
  }

  const onDeleteActionHandler = async (item: NominationModel) => {
    getConfirmation(ConfirmationDialogs.DELETE).then(async () => {
      await deleteNomination(item).then(async response => {
        if (response.success) {
          info(t('nomination:notifications.success.deleteMessage'))
          getNominations()
        } else {
          response?.error
            ? showErrorDetailListDialog(t('nomination:notifications.error.deleteError'), [
                response.error
              ])
            : notifyError(t('nomination:notifications.error.getDataError'))
        }
      })
      closeConfirmation()
    })
  }

  const onDeleteMassiveActionHandler = async (items: NominationModel[]) => {
    if (canDeleteMassive(items)) {
      if (items.length <= 100) {
        // #71523 - El Batch sólo soporta un máximo de 100 items. Si se necesita superarlo, habría que pasar la llamada a través de pioneer y que este fraccione las llamadas.
        getConfirmation(ConfirmationDialogs.DELETE_MASSIVE).then(async () => {
          await deleteMassive(items).then(async (response: ServiceBatchResponse) => {
            if (response.success) {
              info(t('nomination:notifications.success.deleteMessage'))
            } else {
              showErrorDetailListDialog(
                t('nomination:notifications.error.deleteMassive'),
                response.errors
              )
            }
            getNominations()
          })

          closeConfirmation()
        })
      } else {
        //getConfirmation(ConfirmationDialogs.DELETE_MASSIVE)
        info(t('nomination:notifications.info.deleteMaxError'))
      }
    } else {
      info(t('nomination:notifications.info.deleteError'))
    }
  }

  const onSendActionHandler = async (item: NominationModel) => {
    getConfirmation(ConfirmationDialogs.SEND).then(async () => {
      await send(item).then(async () => {
        info(t('nomination:notifications.success.sendMessage'))
        startRetriesTimer([item])
      })
      closeConfirmation()
    })
  }

  const onSendMassiveActionHandler = async (items: NominationModel[]) => {
    if (canSendMassive(items)) {
      getConfirmation(ConfirmationDialogs.SEND_MASSIVE).then(async () => {
        await sendMassive(items).then(async () => {
          info(t('nomination:notifications.success.sendMessage'))
          startRetriesTimer(items)
        })
        closeConfirmation()
      })
    } else {
      info(t('nomination:notifications.info.sendError'))
    }
  }

  //#endregion

  const dataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 1,
    print: false,
    filterType: FilterType.ADVANCED,
    download: true,
    filename: normalizeFilename(t('nomination:title')),
    selectableRows: true,
    canEdit: false,
    canDelete: false,
    rowsPerPage: dataTableRowsPerPage || 100,
    rowsPerPageOptions: [50, 100, 200],
    analytics: {
      customer: getCustomerDomain(),
      view: t('nomination:dataTable.analytics')
    },
    fabLabel: t('nomination:dataTable.fab'),
    fabCustomActions: [
      {
        userPermission: UserPermission.EDIT,
        icon: <Add fontSize="small" />,
        tooltip: t('nomination:dataTable.fabCustomActions.add'),
        onClick: onAddNominationClickHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <PlusOne fontSize="small" />,
        tooltip: t('nomination:dataTable.fabCustomActions.addMassive'),
        onClick: onAddMassiveNominationsClickHandler
      }
    ],
    rowCustomActions: [
      {
        userPermission: UserPermission.SEND,
        icon: <SendIcon fontSize="small" />,
        tooltip: t('nomination:dataTable.customActions.send'),
        visibleIf: 'canSend',
        onClick: onSendActionHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <BlockIcon fontSize="small" />,
        tooltip: t('nomination:dataTable.customActions.cancel'),
        visibleIf: 'canCancel',
        onClick: onCancelActionHandler
      },
      // {
      //   userPermission: UserPermission.EDIT,
      //   icon: <CallSplit fontSize="small" />,
      //   tooltip: t('nomination:dataTable.customActions.allocate'),
      //   visibleIf: 'canAllocate',
      //   onClick: onAllocateActionHandler
      // },
      {
        userPermission: UserPermission.EDIT,
        visibleIf: 'canEdit',
        icon: <Edit fontSize="small" />,
        tooltip: t('nomination:dataTable.customActions.edit'),
        onClick: (row: NominationModel) => {
          row.state === EnumOperationState.Borrador
            ? onEditDraftNominationClickHandler(row)
            : onEditNominationClickHandler(row)
        }
      },
      {
        userPermission: UserPermission.EDIT,
        visibleIf: 'canDelete',
        icon: <Delete fontSize="small" />,
        tooltip: t('nomination:dataTable.customActions.delete'),
        onClick: onDeleteActionHandler
      }
    ],
    selectToolbarCustomActions: [
      {
        userPermission: UserPermission.SEND,
        icon: <SendIcon fontSize="small" />,
        tooltip: t('nomination:dataTable.customActions.send'),
        onClick: onSendMassiveActionHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <BlockIcon fontSize="small" />,
        tooltip: t('nomination:dataTable.customActions.cancel'),
        onClick: onCancelMassiveActionHandler
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <Delete fontSize="small" />,
        tooltip: t('nomination:dataTable.customActions.delete'),
        onClick: onDeleteMassiveActionHandler
      }
    ],
    onFilterChange: (filter: ColumnFilter[]) => {
      storeDataTableFilterForCurrentPage(filter)
    },
    onSearchChange: (search: string) => {
      storeDataTableSearchForCurrentPage(search)
    }
  }

  const dataTableFlangesOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: false,
    print: false,
    filterType: FilterType.NONE,
    download: false,
    selectableRows: false,
    analytics: {
      customer: getCustomerDomain(),
      view: t('nomination:dialogs.flanges.dataTable.analytics')
    }
  }

  useEffect(() => {
    getNominations()
    fetchLogisticElementsOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, activeBalanceZone, activeCompany])

  useEffect(() => {
    updateFormDefinition(addFormDefinition, formDependencies.formState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addFormDefinition])

  useEffect(() => {
    updateFormDefinition(addMassiveFormDefinition, formDependencies.formState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addMassiveFormDefinition])

  useEffect(() => {
    updateFormDefinition(editFormDefinition, formDependencies.formState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editFormDefinition])

  useEffect(() => {
    updateFormDefinition(editDraftFormDefinition, formDependencies.formState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDraftFormDefinition])

  //#endregion

  //#region DataTable definition

  const dataTableColumns: DataTableColumn[] = [
    {
      name: 'actions',
      label: '',
      type: DataType.HTML,
      columnWidth: 25
    },
    {
      name: 'date',
      label: t('nomination:dataTable.columns.date'),
      type: DataType.DATE
    },
    {
      name: 'programmingType',
      label: t('nomination:dataTable.columns.operationType'),
      hideForResponsive: true,
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArrayDefaultValues(
        EnumTipoOperacion,
        'enums:EnumTipoOperacion',
        getNominationFilter()
      )
    },
    {
      name: 'logisticElement.description',
      label: t('nomination:dataTable.columns.logisticElement'),
      type: DataType.TEXT,
      columnWidth: 50
    },
    {
      name: 'quantity',
      label: t('nomination:dataTable.columns.quantity'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.ENERGY
    },
    {
      name: 'sendQuantity',
      label: t('nomination:dataTable.columns.sendQuantity'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.ENERGY,
      hideForResponsive: true
    },
    {
      name: 'sendQuantityTSO',
      label: t('nomination:dataTable.columns.sendQuantityTSO'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.ENERGY,
      hideForResponsive: true
    },
    {
      name: 'state',
      label: t('nomination:dataTable.columns.state'),
      type: DataType.MULTISELECT,
      selectOptions: enumToSelectOptionArray(EnumOperationState, 'enums:EnumOperationState')
    }
  ]

  //#endregion

  //#region Datatable flanges definition

  const dataTableFlangesColumns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'operationType',
      label: t('nomination:dialogs.flanges.dataTable.columns.operationType'),
      hideForResponsive: true,
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumTipoOperacion, 'enums:EnumTipoOperacion')
    },
    {
      name: 'counterpart',
      label: t('nomination:dialogs.flanges.dataTable.columns.counterpart'),
      type: DataType.TEXT
    },
    {
      name: 'quantity',
      label: t('nomination:dialogs.flanges.dataTable.columns.quantity'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.ENERGY
    },
    {
      name: 'operationState',
      label: t('nomination:dialogs.flanges.dataTable.columns.state'),
      hideForResponsive: true,
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumOperationState, 'enums:EnumOperationState')
    }
  ]

  //#endregion

  //#region Functions

  const getNominations = async () => {
    setBalanceStatus(prevState => ({ ...prevState, displayTime: moment().toDate() }))
    const response =
      customFilter !== undefined
        ? await fetchNominationOperations({
            dateRange: { start: customFilter.startDate, end: customFilter.endDate },
            operationTypes: customFilter.operationTypes,
            logisticElement: customFilter.logisticElement
          })
        : await fetchNominationOperations({ dateRange: dateRange })

    // Check balanceStatus, if balanceStatus is inProcess, wait for 3 seconds, if not, set nominations.
    // If after the 3 seconds balanceStatus is not inProcess anymore, refetch nominations.

    // Give time to the balance to update the balanceStatus
    await new Promise(resolve => setTimeout(resolve, 500))
    if (balanceInProcess.current === undefined) {
      // BalanceStatus is delayed and we have no idea if it is inProcess or not, so we will show the nominations
      setNominations(response)
    } else if (balanceInProcess.current) {
      await new Promise(resolve => setTimeout(resolve, 4000))
      if (!balanceInProcess.current && nominationsNeedUpdate.current) {
        getNominations()
      } else {
        setNominations(response)
      }
    } else if (nominationsNeedUpdate.current) {
      getNominations()
    } else {
      setNominations(response)
    }
    nominationsNeedUpdate.current = false
  }

  const monitorOperations = async (pendingOperations: NominationListItemViewModel[]) => {
    let currentOperations = nominations

    return await checkOperationsState(
      pendingOperations.map(op => op.id),
      [EnumOperationState.Enviada, EnumOperationState.Rechazado, EnumOperationState.EnviadoKO]
    ).then(changedOperations => {
      changedOperations.forEach((changedOperation: NominationListItemViewModel) => {
        if (currentOperations && currentOperations !== undefined) {
          const index = currentOperations.findIndex(x => x.id === changedOperation.id)

          currentOperations = [
            ...currentOperations!.slice(0, index),
            completeDataTableActions({ ...changedOperation }),
            ...currentOperations!.slice(index + 1)
          ]
          setNominations(currentOperations)
        }

        const pendingIndex = pendingOperations.findIndex(x => x.id === changedOperation.id)

        pendingOperations = [
          ...pendingOperations!.slice(0, pendingIndex),
          ...pendingOperations!.slice(pendingIndex + 1)
        ]
      })

      return pendingOperations
    })
  }

  const fetchNominationOperations = async ({
    dateRange,
    operationTypes,
    logisticElement
  }: {
    dateRange: Partial<DatePeriod>
    operationTypes?: number[]
    logisticElement?: number
  }): Promise<NominationModel[] | null> => {
    setIsFetching(true)
    setNominations(null)

    const operationTypesFormatted =
      operationTypes && operationTypes.length > 0
        ? operationTypes.map(type => enumValueToKey(EnumTipoOperacion, type))
        : undefined

    return await fetchNominationListItemsV2({
      nominationTypes: getNominationFilter().map(x => enumValueToKey(EnumTipoOperacion, x)),
      dateRange: dateRange,
      operationTypes: operationTypesFormatted,
      logisticElement: logisticElement
    }).then(response => {
      setIsFetching(false)
      if (response.success) {
        clearError()
        if (response.data !== undefined) {
          return response.data?.map((item: NominationModel) => {
            return completeDataTableActions(item)
          })
        } else {
          return []
        }
      } else {
        response.error &&
          showError({
            errorCode: response.error.code,
            onRefresh: async () => {
              setNominations(
                await fetchNominationOperations({ dateRange, operationTypes, logisticElement })
              )
            }
          })
        return []
      }
    })
  }

  const completeDataTableActions = (item: NominationModel): NominationListItemViewModel => {
    return {
      ...item,
      state: [item.state],
      actions:
        item.actionsConfiguration &&
        item.actionsConfiguration.length > 0 &&
        generateIconsFromConfiguration(
          item,
          filterApplicableActionsConfiguration(item, item.actionsConfiguration),
          new Map<ActionIcons, Function>([[ActionIcons.FLANGES, onViewFlangesHandler]])
        )
    }
  }

  const countNominationOperations = async (
    startDate: Date,
    endDate: Date,
    operationType?: number,
    logisticElement?: number
  ): Promise<number | undefined> => {
    return await countNominationListItems(
      getNominationFilter().map(x => enumValueToKey(EnumTipoOperacion, x)),
      {
        start: startDate,
        end: endDate
      },
      enumValueToKey(EnumTipoOperacion, operationType),
      logisticElement
    ).then(response => {
      if (response.success) {
        if (response.data !== undefined) {
          return response.data
        } else {
          return 0
        }
      } else {
        return undefined
      }
    })
  }

  // Fetch all logistic elements.-
  const fetchLogisticElementsOptions = async () => {
    const operationTypes = enumToSelectOptionArrayDefaultValues(
      EnumTipoOperacion,
      'enums:EnumTipoOperacion',
      getNominationFilter()
    )

    if (operationTypes && operationTypes.length < 1) {
      setLogisticElementsToOperationTypes([])
      return
    }

    const operationTypesIds = operationTypes.map(operationType => operationType.value as number)

    const logisticElements = await fetchLogisticElementsSelectOption(operationTypesIds)

    setLogisticElementsToOperationTypes(logisticElements)
  }

  const searchFilterInputs: Input[] = [
    {
      name: 'operationTypes',
      label: t('nomination:forms.crud.inputs.operationType'),
      type: DataType.MULTISELECT,
      clearable: true,
      selectOptions: enumToSelectOptionArrayDefaultValues(
        EnumTipoOperacion,
        'enums:EnumTipoOperacion',
        getNominationFilter()
      )
    },
    {
      name: 'logisticElement',
      label: t('nomination:forms.crud.inputs.logisticElement'),
      type: DataType.MULTISELECT,
      selectOptions: logisticElementsSelectOptions,
      clearable: true,
      isLoading: isFetchingLogisticElements,
      helpText: t('translation:components.searchFilter.helpTexts.selectOperationType')
    }
  ]

  //#endregion

  const fetchNominations = async (filterToApply: {
    startDate: Date
    endDate: Date
    operationTypes?: EnumOperationType[]
    logisticElement?: any
  }) => {
    return await fetchNominationOperations({
      dateRange: { start: filterToApply.startDate, end: filterToApply.endDate },
      operationTypes: filterToApply.operationTypes,
      logisticElement: filterToApply.logisticElement
    })
  }

  const handleShowResults = (results: any[]) => {
    // Remount the DataTable so the previous row selection is cleared
    setDataTableKey(prev => prev + 1)
    setNominations(results)
  }

  //Eventos
  useEffect(() => {
    //getEventsReceived, addEventListener, deleteEventListener
    const eventReceived = (event: string, date: Date, data: any) => {
      switch (event) {
        case 'UpdatedBalance': {
          setBalanceStatus(prevState => ({
            ...prevState,
            inProcess: false,
            lastExecutionTime: date
          }))
          balanceInProcess.current = false
          nominationsNeedUpdate.current = true
          break
        }

        case 'UpdatingBalance': {
          setBalanceStatus(prevState => ({
            ...prevState,
            inProcess: true,
            lastExecutionTime: date
          }))
          balanceInProcess.current = true
          break
        }
      }
    }

    addEventListener('Balance', [], { onEventReceivedHandler: eventReceived } as OnEventReceived)

    const lastEventReceived = getEventsReceived([
      'UpdatedBalance',
      'UpdatingBalance',
      'UpdateFailedBalance'
    ])
    if (lastEventReceived && lastEventReceived?.length > 0) {
      switch (lastEventReceived[0].event) {
        case 'UpdatedBalance': {
          setBalanceStatus(prevState => ({
            ...prevState,
            inProcess: false,
            lastExecutionTime: lastEventReceived[0].date
          }))
          nominationsNeedUpdate.current = true
          balanceInProcess.current = false
          break
        }
        case 'UpdatingBalance': {
          setBalanceStatus(prevState => ({
            ...prevState,
            inProcess: true,
            lastExecutionTime: lastEventReceived[0].date
          }))
          balanceInProcess.current = true
          break
        }
      }
    } else {
      getStatusBalance().then(status => {
        setBalanceStatus(prevState => ({
          ...prevState,
          inProcess: status?.inProcess ?? false,
          lastExecutionTime: status?.lastExecutionTime
        }))
      })
    }

    return () => {
      deleteEventListener()
    }
  }, [])

  useEffect(() => {
    if (balanceStatus && balanceStatus.lastExecutionTime) {
      if (balanceStatus.inProcess) {
        setGlobalNotification({
          icon: NotificationMessageIcons.LOADING,
          message: t('balance:globalNotifications.calculation.messages.onCalculation')
        })
      } else if (
        (balanceStatus.displayTime ?? moment().toDate()) < balanceStatus.lastExecutionTime
      ) {
        setGlobalNotification({
          icon: NotificationMessageIcons.ERROR,
          message: t('balance:globalNotifications.calculation.messages.onFinish'),
          actions: [
            {
              label: t('balance:globalNotifications.calculation.actions.refresh'),
              onClick: async () => {
                getNominations()
              }
            }
          ]
        })
      } else {
        setGlobalNotification(undefined)
      }
    }
  }, [balanceStatus])

  return (
    <Page notification={globalNotification}>
      <PageHeader title={t('nomination:title')}>
        <DateFilter
          inactive={customFilter}
          enabled={!isFetching}
          initialFilter={
            dateFilter?.activeDateFilter === null || dateFilter?.activeDateFilter === undefined
              ? DateFilters.TODAY
              : dateFilter.activeDateFilter
          }
          onSelectDate={onSelectDateHandler}
          filters={[
            {
              filter: DateFilters.TODAY
            },
            {
              filter: DateFilters.TOMORROW
            },
            {
              filter: DateFilters.WEEK
            }
          ]}
        ></DateFilter>
        <CustomFilterV2
          onFetchData={fetchNominations}
          onShowResults={handleShowResults}
          additionalFilters={searchFilterInputs}
          appliedFilter={customFilter}
          enabled={!isFetching}
        />
      </PageHeader>

      <DataTable
        key={dataTableKey}
        data={nominations}
        columns={dataTableColumns}
        options={dataTableOptions}
        info={DataTableErrorHandler(errorProps)}
        userPermissions={getUserPermissions(Pages.Nominations)}
        appliedFilter={dataTableFilter}
        appliedSearch={dataTableSearch}
      />

      <CustomFormDialog {...formProps}></CustomFormDialog>

      <Dialog
        fullWidth
        disableBackdropClick
        disableEscapeKeyDown
        open={showFlangeDialog}
        className={classes.root}
      >
        <DialogContent>
          <Typography variant="h1" color="secondary">
            {t('nomination:dialogs.flanges.title')}
          </Typography>
          <DataTable
            data={flanges}
            columns={dataTableFlangesColumns}
            options={dataTableFlangesOptions}
          />
        </DialogContent>
        <DialogActions className={classes.dialogActionsRoot}>
          <Button size="small" onClick={() => setShowFlangeDialog(false)}>
            {t('nomination:dialogs.flanges.actions.close')}
          </Button>
          <Button
            size="small"
            onClick={() => {
              history.push(NonAuthRoutes.buyAndSell, { ids: flanges?.map(item => item.id) })
            }}
            color="primary"
          >
            {t('nomination:dialogs.flanges.actions.viewOperations')}
          </Button>
        </DialogActions>
      </Dialog>
      <ErrorDetailList {...errorDetailListDialogProps}></ErrorDetailList>
    </Page>
  )
}
